/**
 * **O contrato da área visível do mundo aberto** (gate V28I.AN.1, alargado nos
 * gates V28I.BX.2 e V28I.BX.2.1 — ADR 0101 e ADR 0125).
 *
 * Antes deste ficheiro, "quanto é que um jogador vê" não estava escrito em
 * lado nenhum. A cena não chamava `setZoom`, portanto o zoom era o implícito do
 * Phaser; a escala era `RESIZE` com `100% x 100%`, portanto a área visível era
 * o tamanho da janela; e nenhum ficheiro declarava um máximo. A área de
 * interesse do servidor não tinha, por isso, de onde derivar — e o gate V28I.AN
 * parou por causa disso.
 *
 * ## Duas áreas, e não uma
 *
 * - **A vista canónica: 3840 x 2160 pixeis lógicos a zoom 1.** A decisão
 *   original do dono. É o que `openWorldZoomFor` respeita e o que a garantia
 *   das mecânicas mede. **Não muda** quando o jogador se afasta: o afastamento
 *   é apresentação, e uma mecânica ancorada nele daria vantagem a quem se
 *   afasta.
 * - **A área visível máxima: 6144 x 3456.** O que o jogador pode chegar a ver
 *   com a roda. O gate V28I.BX.2 pôs o afastamento em 0,5 — numa janela
 *   1920 x 1080, a própria vista canónica — e o gate V28I.BX.2.1 pediu 60 %
 *   mais de distância do que isso: `0,5 / 1,6 = 0,3125`, e `3840 x 1,6 = 6144`,
 *   `2160 x 1,6 = 3456`. É dela que deriva a área de interesse, porque é a ela
 *   que nada pode aparecer do nada.
 *
 * É um contrato de ÁREA VISÍVEL e não um limite de monitor. Numa janela maior
 * a tela continua a encher a janela inteira: o que muda é o zoom, que
 * **aumenta** automaticamente para que a porção de mundo mostrada nunca passe
 * do envelope. Não há tarja preta, não há distorção, não há redução de nenhum
 * efeito, e a interface em DOM não é afetada — ela vive fora da tela.
 *
 * ## As quatro unidades, que não são a mesma coisa
 *
 * | unidade | o que é |
 * | --- | --- |
 * | pixel físico | o que o painel tem. Depende do `devicePixelRatio`. |
 * | pixel lógico CSS | o que `Phaser.Scale.width` devolve. **É a unidade deste contrato.** |
 * | unidade de mundo | o eixo em que a cena desenha. A zoom 1, uma unidade é um pixel lógico. |
 * | `mu` | um milésimo de unidade de mundo. É a unidade do domínio. |
 *
 * O pixel FÍSICO não entra aqui de propósito: um ecrã 4K a 200 % de escala do
 * sistema entrega 1920 x 1080 pixeis lógicos, e é isso que o jogador vê do
 * mundo. Ligar o contrato ao pixel físico faria dois computadores com o mesmo
 * monitor mostrarem porções diferentes do quadrante.
 *
 * ## Porque é que o servidor usa o contrato e não a janela de cada um
 *
 * A área de interesse é uma só, do lado autoritativo, e tem de ser igual para
 * toda a gente. Fazê-la depender da janela — ou do zoom — de quem pergunta
 * deixaria um jogador encolher a janela para ficar invisível ou afastar-se para
 * ser servido de mais longe do que os outros. Quem joga numa janela pequena, ou
 * mais aproximado, recebe mais do que mostra, que é o lado errado do erro e o
 * único aceitável.
 */

/** Quantos `mu` valem uma unidade de mundo. O mesmo mil de `PVP_MU_PER_UNIT`. */
export const OPEN_WORLD_MU_PER_PIXEL = 1_000;

/** A largura da vista canónica, em pixeis lógicos: o contrato de zoom 1. */
export const OPEN_WORLD_CANONICAL_WIDTH_PX = 3840;

/** A altura da vista canónica, em pixeis lógicos. */
export const OPEN_WORLD_CANONICAL_HEIGHT_PX = 2160;

/**
 * **Quanto a área visível máxima alarga a canónica, como fração: 8/5.**
 *
 * Os 60 % a mais do gate V28I.BX.2.1, escritos como fração para que as duas
 * dimensões fiquem inteiras e a diagonal saia de uma raiz inteira.
 */
const WIDENING_NUMERATOR = 8;
const WIDENING_DENOMINATOR = 5;

/** A largura MÁXIMA da área visível, em pixeis lógicos: 6144. */
export const OPEN_WORLD_VISIBLE_WIDTH_PX =
  (OPEN_WORLD_CANONICAL_WIDTH_PX * WIDENING_NUMERATOR) / WIDENING_DENOMINATOR;

/** A altura MÁXIMA da área visível, em pixeis lógicos: 3456. */
export const OPEN_WORLD_VISIBLE_HEIGHT_PX =
  (OPEN_WORLD_CANONICAL_HEIGHT_PX * WIDENING_NUMERATOR) / WIDENING_DENOMINATOR;

/**
 * **O zoom mínimo da vista canónica, escrito como decisão.**
 *
 * Era o valor implícito do Phaser, e um valor implícito não é uma decisão: nada
 * impedia um gate futuro de afastar a câmara e alargar a área visível sem que
 * ninguém notasse que a área de interesse do servidor tinha deixado de a cobrir.
 * A cena chama `setZoom` com o canónico multiplicado pelo zoom do jogador, e
 * nunca abaixo de {@link openWorldEnvelopeZoomFor}.
 */
export const OPEN_WORLD_MIN_ZOOM = 1;

/**
 * **A raiz quadrada inteira, arredondada para cima.** Sem vírgula flutuante no
 * resultado.
 *
 * `Math.sqrt` é definido pela implementação no último bit, e o Node e o Chromium
 * divergem lá — a mesma regra que obriga a comparar flutuantes a quatro casas na
 * digestão. Aqui o valor é um ALCANCE que o servidor usa para decidir quem entra
 * numa trama, portanto tem de ser o mesmo inteiro em toda a parte. A raiz
 * flutuante serve só de palpite, e a correção por inteiros fecha-o.
 */
function ceilSqrt(value: number): number {
  let root = Math.floor(Math.sqrt(value));
  while (root * root > value) {
    root -= 1;
  }
  while (root * root < value) {
    root += 1;
  }
  return root;
}

/**
 * **O alcance radial MÁXIMO da câmara, em `mu`: 7 049 303.**
 *
 * A diagonal INTEIRA da área visível máxima, e não a meia-diagonal.
 *
 * O gate V28I.AN estimou a meia-diagonal porque presumiu que o casco fica
 * sempre no centro do ecrã. Ele não fica. `OpenWorldScene.followCamera` prende
 * o centro da câmara ao quadrante — `max(meiaLargura, min(lado - meiaLargura, x))`
 * — e num canto do quadrante o casco encosta a `(0, 0)` enquanto a câmara fica
 * presa em `(meiaLargura, meiaAltura)`. O casco passa a estar no CANTO do ecrã, e
 * o pixel visível mais longe dele é o canto oposto: a diagonal toda.
 *
 * Provado pelo clamp real, com o envelope 6144 x 3456 (a 0,3125 numa janela
 * 1920 x 1080):
 *
 * | onde está o casco | pixel visível mais longe |
 * | --- | ---: |
 * | centro | 3 524,6 px |
 * | borda superior ou inferior | 4 624,0 px |
 * | borda esquerda ou direita | 6 382,4 px |
 * | **qualquer um dos quatro cantos** | **7 049,3 px** |
 *
 * O canto é alcançável: `clampToQuadrant` deixa o centro de um casco chegar a
 * zero exatamente.
 *
 * É o número da COBERTURA — o que a área de interesse tem de conter para que
 * nada apareça do nada. Para a pergunta inversa, ver
 * {@link OPEN_WORLD_VISIBLE_GUARANTEED_MU}.
 */
export const OPEN_WORLD_VISIBLE_REACH_MU = ceilSqrt(
  (OPEN_WORLD_VISIBLE_WIDTH_PX * OPEN_WORLD_VISIBLE_WIDTH_PX +
    OPEN_WORLD_VISIBLE_HEIGHT_PX * OPEN_WORLD_VISIBLE_HEIGHT_PX) *
    OPEN_WORLD_MU_PER_PIXEL *
    OPEN_WORLD_MU_PER_PIXEL,
);

/**
 * **O alcance radial GARANTIDO, em `mu`: 1 080 000.**
 *
 * O maior raio `r` tal que **tudo** o que estiver a menos de `r` do casco está
 * no ecrã da VISTA CANÓNICA, em qualquer direção e esteja o casco onde estiver
 * no quadrante: metade da menor dimensão de 3840 x 2160.
 *
 * | onde está o casco | disco garantido, dentro do quadrante |
 * | --- | ---: |
 * | centro | 1 080 px (a meia-altura) |
 * | borda esquerda ou direita | 1 080 px (a meia-altura) |
 * | borda superior ou inferior | 1 920 px (a meia-largura) |
 * | canto | 2 160 px (a altura inteira) |
 *
 * **Este é o número das MECÂNICAS**, e é por isso que continua ancorado à vista
 * canónica e não à área máxima (gate V28I.BX.2.1). O afastamento da roda é
 * apresentação: se a deteção de um alienígena crescesse com ele, afastar-se
 * mudaria quem é perseguido — uma vantagem mecânica, e uma alteração de
 * alcance, que o dono proibiu. O outro número —
 * {@link OPEN_WORLD_VISIBLE_REACH_MU} — é o máximo, e usá-lo aqui prometeria que
 * um alienígena só ataca quando está no ecrã e depois deixaria de o cumprir em
 * quase todas as posições.
 */
export const OPEN_WORLD_VISIBLE_GUARANTEED_MU =
  (Math.min(OPEN_WORLD_CANONICAL_WIDTH_PX, OPEN_WORLD_CANONICAL_HEIGHT_PX) / 2) *
  OPEN_WORLD_MU_PER_PIXEL;

/**
 * **O zoom mais afastado que a área visível máxima permite** (gates V28I.BX.2
 * e V28I.BX.2.1).
 *
 * `max(largura / 6144, altura / 3456)` — composto por EIXO, como
 * {@link openWorldZoomFor}, sem o chão de 1. É o chão do afastamento do
 * jogador: com ele, a porção de mundo mostrada nunca passa de 6144 x 3456 em
 * nenhum eixo, e a área de interesse cobre a diagonal inteira disso.
 *
 * Numa janela 1920 x 1080 dá 0,3125 — 60 % mais distância do que o 0,5 do
 * gate V28I.BX.2, exatamente o que o dono pediu. Numa janela maior o chão sobe
 * — ela já via mais — e numa de 6144 x 3456 é 1: não há mais nada para mostrar.
 */
export function openWorldEnvelopeZoomFor(widthPx: number, heightPx: number): number {
  return Math.max(widthPx / OPEN_WORLD_VISIBLE_WIDTH_PX, heightPx / OPEN_WORLD_VISIBLE_HEIGHT_PX);
}

/**
 * **O zoom da vista canónica numa janela.**
 *
 * `max(1, largura / 3840, altura / 2160)` — composto por EIXO, e não pela
 * diagonal.
 *
 * A diagonal sozinha não chega, e o erro não é teórico. Uma janela
 * 7680 x 1080 tem diagonal 7755 px; limitá-la pela diagonal daria zoom 1,760 e
 * mostraria 4363 x 614 pixeis de mundo — **523 px de largura acima do
 * contrato**, exatamente no eixo em que ela é extrema. A composição por eixo dá
 * zoom 2,000 e mostra 3840 x 540: dentro do envelope nos dois eixos, e com uma
 * diagonal visível que nunca passa da diagonal do próprio envelope.
 *
 * O zoom AUMENTA a imagem. Uma janela maior do que o envelope não passa a ver
 * mais mundo: passa a ver o mesmo mundo maior, que é o que um contrato de área
 * visível quer dizer. Nada é cortado, nada é encolhido e nada é escondido.
 *
 * **Não mudou com o afastamento** (gate V28I.BX.2.1): é a vista em que uma
 * expedição começa, e a roda multiplica-a.
 */
export function openWorldZoomFor(widthPx: number, heightPx: number): number {
  return Math.max(
    OPEN_WORLD_MIN_ZOOM,
    widthPx / OPEN_WORLD_CANONICAL_WIDTH_PX,
    heightPx / OPEN_WORLD_CANONICAL_HEIGHT_PX,
  );
}
